/**
 * Admin translations API — the write side of the content overlay.
 *
 * Reads and saves `content_translations` rows for one entity at a time (or many
 * at once for a list view). The render-time overlay that consumes these lives
 * entirely on the server; nothing here is used by the front end proper.
 */

const API_BASE = '/api';

/** A non-source language an admin can author into. English is never offered. */
export interface TranslatableLocale {
  code: string;
  englishName: string;
  nativeName: string;
}

/** field_path → locale → value. Absent means untranslated. */
export type TranslationValues = Record<string, Record<string, string>>;

export interface TranslationsResponse {
  locales: TranslatableLocale[];
  /** entity_id (as a string key) → field_path → locale → value */
  translations: Record<string, TranslationValues>;
}

async function handle<T>(res: Response): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string })?.error || `Request failed (${res.status})`);
  return data as T;
}

/**
 * Fetch stored translations for one or more rows of an entity type.
 *
 * Takes a list rather than a single id so an editor showing many rows makes one
 * request — the endpoint resolves them in a single query.
 */
export async function fetchTranslations(
  entityType: string,
  entityIds: number[],
): Promise<TranslationsResponse> {
  const ids = entityIds.length ? `&entity_id=${entityIds.join(',')}` : '';
  return handle<TranslationsResponse>(
    await fetch(`${API_BASE}/admin-translations.php?entity_type=${encodeURIComponent(entityType)}${ids}`, {
      credentials: 'include',
    }),
  );
}

/**
 * Save translations for one entity.
 *
 * A blank value CLEARS that cell rather than storing an empty string — blank
 * means "untranslated", which is the absence of a row. Send only the paths the
 * editor actually manages; anything omitted is left untouched.
 */
export async function saveTranslations(
  entityType: string,
  entityId: number,
  values: TranslationValues,
): Promise<{ saved: number; cleared: number }> {
  return handle<{ saved: number; cleared: number }>(
    await fetch(`${API_BASE}/admin-translations.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityType, entityId, values }),
    }),
  );
}

/** Drop every translation for a row — call when the row itself is deleted. */
export async function deleteTranslations(entityType: string, entityId: number): Promise<void> {
  await handle(
    await fetch(
      `${API_BASE}/admin-translations.php?entity_type=${encodeURIComponent(entityType)}&entity_id=${entityId}`,
      { method: 'DELETE', credentials: 'include' },
    ),
  );
}
